import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { PaymentFormContent } from "@/components/settings/admin/stats/payments/form/PaymentFormContent";
import { insertPaymentWithBeneficiaries } from "@/lib/payments/insertGroupPayment";

interface RegisterPaymentDialogProps {
  gymId: string;
}

interface MemberOption {
  id: string;
  name: string;
  source: "profile" | "pending";
}

export const RegisterPaymentDialog = ({ gymId }: RegisterPaymentDialogProps) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();
  const [open, setOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);

  const { data: members } = useQuery({
    queryKey: ["gym-members-for-payment", gymId],
    queryFn: async () => {
      const [profilesRes, pendingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, first_name, last_name, username")
          .eq("gym_id", gymId),
        supabase
          .from("pending_members")
          .select("id, first_name, last_name, dni")
          .eq("gym_id", gymId),
      ]);
      const profiles: MemberOption[] = (profilesRes.data || []).map((p) => ({
        id: p.id,
        name:
          [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
          p.username?.split("@")[0] ||
          "Sin nombre",
        source: "profile" as const,
      }));
      const pending: MemberOption[] = (pendingRes.data || [])
        .filter((p: any) => p.dni !== "__WALKIN__")
        .map((p) => ({
          id: p.id,
          name:
            [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
            "Sin nombre",
          source: "pending" as const,
        }));
      return [...profiles, ...pending].sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!gymId && open,
  });

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    const q = memberSearch.toLowerCase().trim();
    if (!q) return members.slice(0, 20);
    return members.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 20);
  }, [members, memberSearch]);

  const reset = () => {
    setSelectedMember(null);
    setMemberSearch("");
  };

  const handleSubmit = async (data: {
    amount: number;
    discount: number;
    description: string;
    validity_days: number;
    paid_at: string;
    payment_method: string;
    covered_user_ids: string[];
  }) => {
    if (!selectedMember) return;
    try {
      await insertPaymentWithBeneficiaries({
        payerId: selectedMember.id,
        amount: data.amount,
        discount: data.discount,
        description: data.description,
        validityDays: data.validity_days,
        paidAt: data.paid_at,
        paymentMethod: data.payment_method,
        coveredUserIds: data.covered_user_ids,
        payerDisplayName: selectedMember.name,
      });

      const extras = data.covered_user_ids?.length || 0;
      success("Pago registrado", {
        description:
          extras > 0
            ? `Pago grupal: ${extras + 1} socios quedaron al día`
            : "El pago ha sido registrado exitosamente",
      });

      await queryClient.invalidateQueries({ queryKey: ["payments", gymId] });
      await queryClient.invalidateQueries({ queryKey: ["lastPayment"] });
      await queryClient.invalidateQueries({ queryKey: ["user-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["arqueo-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["arqueo-closure"] });

      reset();
      setOpen(false);
    } catch (e) {
      console.error("Error al registrar el pago:", e);
      showError("No se pudo registrar el pago");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <CreditCard className="h-4 w-4" />
          Registrar pago
        </Button>
      </DialogTrigger>
      <DialogContent
        className="w-[95%] max-w-lg mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {selectedMember
              ? `Registrar pago para ${selectedMember.name}`
              : "Registrar pago"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Socio</label>
            {selectedMember ? (
              <div className="flex items-center justify-between p-2 border rounded-md bg-muted/40">
                <span className="text-sm">{selectedMember.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setSelectedMember(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Buscar socio..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                {memberSearch && (
                  <div className="border rounded-md max-h-48 overflow-y-auto">
                    {filteredMembers.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">
                        Sin resultados
                      </p>
                    ) : (
                      filteredMembers.map((m) => (
                        <button
                          key={`${m.source}-${m.id}`}
                          type="button"
                          onClick={() => {
                            setSelectedMember(m);
                            setMemberSearch("");
                          }}
                          className="w-full text-left p-2 text-sm hover:bg-muted transition-colors"
                        >
                          {m.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {selectedMember && (
            <PaymentFormContent
              gymId={gymId}
              payerId={selectedMember.id}
              onSubmit={handleSubmit}
              onCancel={() => setOpen(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
